// src/app/services/store-settings.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { dasa } from 'src/classess/dasaClasses';

interface ResponseObject<T> {
  success: boolean;
  message: string;
  data: T;
  statusCode: number;
}

@Injectable({ providedIn: 'root' })
export class StoreSettingsApi {
  private base = `${environment.NEW_BACKEND_URL}api/store-settings`;

  constructor(private http: HttpClient) {}

  create(payload: Partial<dasa.StoreSettings>): Observable<dasa.StoreSettings> {
    return this.http.post<ResponseObject<dasa.StoreSettings>>(this.base, payload)
      .pipe(map(res => res.data));
  }

  getById(id: number): Observable<dasa.StoreSettings> {
    return this.http.get<ResponseObject<dasa.StoreSettings>>(`${this.base}/${id}`)
      .pipe(map(res => res.data));
  }

  getByStoreId(storeId: number): Observable<dasa.StoreSettings> {
    return this.http.get<ResponseObject<dasa.StoreSettings>>(`${this.base}/by-store/${storeId}`)
      .pipe(map(res => res.data));
  }

  listAll(): Observable<dasa.StoreSettings[]> {
    return this.http.get<ResponseObject<dasa.StoreSettings[]>>(this.base)
      .pipe(map(res => res.data));
  }

  update(id: number, payload: Partial<dasa.StoreSettings>): Observable<dasa.StoreSettings> {
    return this.http.put<ResponseObject<dasa.StoreSettings>>(`${this.base}/${id}`, payload)
      .pipe(map(res => res.data));
  }

  patch(id: number, payload: Partial<dasa.StoreSettings>): Observable<dasa.StoreSettings> {
    return this.http.patch<ResponseObject<dasa.StoreSettings>>(`${this.base}/${id}`, payload)
      .pipe(map(res => res.data));
  }

  delete(id: number): Observable<void> {
    return this.http.delete<ResponseObject<void>>(`${this.base}/${id}`)
      .pipe(map(() => undefined));
  }

  upsertByStoreId(payload: Partial<dasa.StoreSettings>): Observable<dasa.StoreSettings> {
    return this.http.put<ResponseObject<dasa.StoreSettings>>(`${this.base}/by-store/upsert`, payload)
      .pipe(map(res => res.data));
  }
  getAllCustomers(businessId: number): Observable<ResponseObject<dasa.Customer[]>> {
    let params = new HttpParams().set('businessId', businessId.toString());
    let apiURL = environment.NEW_BACKEND_URL+"api/customer/getAllCustomers"
    return this.http.get<ResponseObject<dasa.Customer[]>>(apiURL, { params });
  }
    getAllCustomersData(businessId: number,storeId: number): Observable<ResponseObject<dasa.CustomerData[]>> {
    let params = new HttpParams().set('businessId', businessId.toString()).set('storeId', storeId.toString());
    let apiURL = environment.NEW_BACKEND_URL+"api/customer/getAllCustomerData"
    return this.http.get<ResponseObject<dasa.CustomerData[]>>(apiURL, { params });
  }

  // ----- Bulk send + email config (CRM) -----------------------------------
  // Both routes live under the new backend and use the store's own WhAPI /
  // SMTP credentials — see WhatsAppBulkController and EmailBulkController
  // on the backend. All numbers get run through the phone normaliser inside
  // the WhatsApp service, so the frontend can send raw stored phones.
  // templateName is required for Meta-only stores: Meta will only carry an
  // approved MARKETING template for a broadcast, never free text.
  bulkSendWhatsApp(payload: {
    storeId: number; businessId: number; phones: string[]; body: string; templateName?: string;
  }): Observable<any> {
    return this.http.post<any>(environment.NEW_BACKEND_URL + 'api/whatsapp/bulk-send', payload);
  }

  // ----- WhatsApp configuration -------------------------------------------

  /** Reads the phone number back from Meta to prove the credentials work. */
  testWhatsAppConfig(storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(environment.NEW_BACKEND_URL + 'api/whatsapp/config/test', { params });
  }

  listWhatsAppTemplates(storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(environment.NEW_BACKEND_URL + 'api/whatsapp/config/templates', { params });
  }

  listWhatsAppMarketingTemplates(storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(
      environment.NEW_BACKEND_URL + 'api/whatsapp/config/templates/marketing', { params });
  }

  syncWhatsAppTemplates(storeId: number): Observable<any> {
    return this.http.post<any>(
      environment.NEW_BACKEND_URL + 'api/whatsapp/config/templates/sync', { storeId });
  }

  whatsAppDeliveryStats(storeId: number, days = 30): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId)).set('days', String(days));
    return this.http.get<any>(environment.NEW_BACKEND_URL + 'api/whatsapp/config/delivery', { params });
  }

  // Returns a jobId immediately rather than sending inline — the campaign is
  // drained in paced batches by the backend scheduler.
  bulkSendEmail(payload: {
    storeId: number; businessId: number; recipients: string[]; subject: string;
    htmlBody: string; replyTo?: string; batchSize?: number; batchIntervalMinutes?: number;
  }): Observable<any> {
    return this.http.post<any>(environment.NEW_BACKEND_URL + 'api/email/bulk-send', payload);
  }

  listCampaigns(storeId: number, limit = 20): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId)).set('limit', String(limit));
    return this.http.get<any>(environment.NEW_BACKEND_URL + 'api/campaigns', { params });
  }

  getCampaign(jobId: number, storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(`${environment.NEW_BACKEND_URL}api/campaigns/${jobId}`, { params });
  }

  campaignAction(jobId: number, storeId: number, action: 'pause' | 'resume' | 'cancel'): Observable<any> {
    return this.http.post<any>(
      `${environment.NEW_BACKEND_URL}api/campaigns/${jobId}/${action}`, { storeId });
  }

  // Last IMAP poll outcome + whether SMTP/IMAP are set up. Consumed by
  // both the Email Configuration page (last-poll card) and the CRM
  // conversation drawer (hint banner when IMAP is missing).
  getImapStatus(storeId: number): Observable<any> {
    let params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(environment.NEW_BACKEND_URL + 'api/email/imap-status', { params });
  }

  // Chronological email thread for one customer at the current store —
  // populates the CRM conversation drawer.
  getEmailThread(storeId: number, email: string): Observable<any[]> {
    let params = new HttpParams()
      .set('storeId', String(storeId))
      .set('email', email);
    return this.http.get<any[]>(environment.NEW_BACKEND_URL + 'api/insights/customers-report/email-thread', { params });
  }

  testEmailConfig(payload: {
    storeId?: number;
    smtpHost: string; smtpPort: number; smtpUsername: string; smtpPassword: string;
    smtpFromAddress?: string; smtpFromName?: string; smtpUseTls?: boolean; smtpUseSsl?: boolean;
    testRecipient: string;
  }): Observable<any> {
    return this.http.post<any>(environment.NEW_BACKEND_URL + 'api/email/test-config', payload);
  }

  // The store's StoreConfigurations row. The email screen used to build this
  // URL by hand against `getStoreConfigurations/{id}`, which the backend has
  // never exposed — the 404 meant the form silently never prefilled.
  getStoreConfig(storeId: number): Observable<any> {
    return this.http.get<ResponseObject<any>>(
      `${environment.NEW_BACKEND_URL}api/storeConfigurations/get/${storeId}`,
    ).pipe(map(res => res.data));
  }

  // Explicit teardown of the per-store SMTP/IMAP block. Needed because
  // createOrUpdate merges with firstNonNull(input, existing, default), so
  // posting nulls there is a no-op and can't clear a saved config.
  // ----- Domain health + warm-up ------------------------------------------

  runDomainCheck(payload: { storeId: number; domain?: string; extraSelectors?: string[] }): Observable<any> {
    return this.http.post<any>(`${environment.NEW_BACKEND_URL}api/email/domain/check`, payload);
  }

  addDkimSelector(payload: { storeId: number; domain: string; selector: string }): Observable<any> {
    return this.http.post<any>(`${environment.NEW_BACKEND_URL}api/email/domain/selectors`, payload);
  }

  /** Whether the store's own SMTP is currently working — drives the fallback banner. */
  getSendHealth(storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(`${environment.NEW_BACKEND_URL}api/email/send-health`, { params });
  }

  getWarmupStatus(storeId: number): Observable<any> {
    const params = new HttpParams().set('storeId', String(storeId));
    return this.http.get<any>(`${environment.NEW_BACKEND_URL}api/email/warmup/status`, { params });
  }

  saveWarmupSettings(payload: {
    storeId: number; enabled?: boolean; enforce?: boolean;
    startedOn?: string; overrideDailyCap?: number | null;
  }): Observable<any> {
    return this.http.post<any>(`${environment.NEW_BACKEND_URL}api/email/warmup/settings`, payload);
  }

  resetWarmup(storeId: number): Observable<any> {
    return this.http.post<any>(`${environment.NEW_BACKEND_URL}api/email/warmup/reset`, { storeId });
  }

  clearSmtpConfig(storeId: number, modifiedBy?: number): Observable<any> {
    let params = new HttpParams();
    if (modifiedBy != null) params = params.set('modifiedBy', String(modifiedBy));
    return this.http.post<any>(
      `${environment.NEW_BACKEND_URL}api/storeConfigurations/clearSmtp/${storeId}`,
      {},
      { params },
    );
  }
}
